import { DateTime } from "luxon";
import type {
  AccessCode,
  AccessCodeShowing,
  GenerateLockboxCodePayload,
  Lockbox,
  LockboxCodeScheduling
} from "api/landlord/lockboxes";
import {
  DEVICE_TYPE_LABELS,
  VENDOR_COMPANY_LABELS
} from "views/lockboxes/constants";

// The Start time dropdown spans 6:00 AM to 9:00 PM in the code's time zone (design, CRM-1727).
const START_TIME_RANGE = { firstMinute: 6 * 60, lastMinute: 21 * 60 };

export const PAST_START_MESSAGE = "That time has passed. Pick a later time.";

const DAY_FORMAT = "ccc, LLL d";
const TIME_FORMAT = "h:mm a";
const DAY_TIME_FORMAT = `${DAY_FORMAT}, ${TIME_FORMAT}`;
const VALID_UNTIL_FORMAT = `LLL d, ${TIME_FORMAT}`;

export interface CodeTimezone {
  zone: string;
  isFallback: boolean;
}

export interface StartTimeOption {
  value: string;
  label: string;
}

const isValidZone = (zone: string) => DateTime.now().setZone(zone).isValid;

export const resolveCodeTimezone = (
  propertyZone?: string | null
): CodeTimezone =>
  propertyZone && isValidZone(propertyZone)
    ? { zone: propertyZone, isFallback: false }
    : { zone: DateTime.local().zoneName ?? "UTC", isFallback: true };

const zoneAbbreviation = (at: DateTime, zone: string) =>
  at.setZone(zone).toFormat("ZZZZ");

export const timezoneLine = (
  { zone, isFallback }: CodeTimezone,
  at: DateTime
) =>
  isFallback
    ? `This property has no time zone, so times are in your computer's time zone (${zoneAbbreviation(at, zone)}).`
    : `Times are in the property's time zone (${zoneAbbreviation(at, zone)}).`;

export const todayInZone = (zone: string, now: DateTime) =>
  now.setZone(zone).toISODate() ?? "";

const addDays = (date: string, days: number) =>
  DateTime.fromISO(date).plus({ days }).toISODate() ?? date;

export const formatDay = (date: string, zone: string) =>
  DateTime.fromISO(date, { zone }).toFormat(DAY_FORMAT);

export const formatInstantDay = (iso: string, zone: string) =>
  DateTime.fromISO(iso).setZone(zone).toFormat(DAY_FORMAT);

export const combineStart = (date: string, time: string, zone: string) =>
  DateTime.fromISO(`${date}T${time}`, { zone });

export const startTimeOptions = ({
  date,
  zone,
  stepMinutes,
  now
}: {
  date: string;
  zone: string;
  stepMinutes: number;
  now: DateTime;
}): StartTimeOption[] => {
  const day = DateTime.fromISO(date, { zone }).startOf("day");
  if (!day.isValid || stepMinutes <= 0) return [];

  // Hourly starts (Igloohome) are whole UTC hours: the API checks them that way and Igloohome
  // floors to the UTC hour. In a zone with a :30/:45 offset they read e.g. 6:30 AM locally.
  const alignment = stepMinutes === 60 ? ((day.offset % 60) + 60) % 60 : 0;
  const firstMinute = START_TIME_RANGE.firstMinute + alignment;
  const slotCount =
    Math.floor((START_TIME_RANGE.lastMinute - firstMinute) / stepMinutes) + 1;

  // A DST change can map two wall-clock slots to one instant; keep the first.
  return Array.from(
    { length: slotCount },
    (_, index) => firstMinute + index * stepMinutes
  )
    .map((minute) =>
      day.set({ hour: Math.floor(minute / 60), minute: minute % 60 })
    )
    .filter(
      (at, index, slots) =>
        at.toISODate() === date &&
        at.toMillis() > now.toMillis() &&
        slots.findIndex((other) => other.toMillis() === at.toMillis()) === index
    )
    .map((at) => ({
      value: at.toFormat("HH:mm"),
      label: at.toFormat(TIME_FORMAT)
    }));
};

export const defaultStartTime = (
  options: StartTimeOption[],
  zone: string,
  now: DateTime
): string | null => {
  const nextFullHour = now
    .setZone(zone)
    .plus({ hours: 1 })
    .startOf("hour")
    .toFormat("HH:mm");
  return (
    options.find((option) => option.value >= nextFullHour)?.value ??
    options[0]?.value ??
    null
  );
};

export const initialCodeDate = ({
  zone,
  scheduling,
  now
}: {
  zone: string;
  scheduling: LockboxCodeScheduling;
  now: DateTime;
}) => {
  const today = todayInZone(zone, now);
  if (scheduling.all_day) return today;
  const hasTimesToday =
    startTimeOptions({
      date: today,
      zone,
      stepMinutes: scheduling.start_step_minutes,
      now
    }).length > 0;
  return hasTimesToday || scheduling.max_days_ahead < 1
    ? today
    : addDays(today, 1);
};

// The Date calendar's range: from the first day that still has a start time (tomorrow once
// today's last start has passed) to today + max_days_ahead. The calendar counts maxDays from
// minDate, so the days skipped at the start come off maxDays.
export const laterDateRange = ({
  zone,
  scheduling,
  now
}: {
  zone: string;
  scheduling: LockboxCodeScheduling;
  now: DateTime;
}) => {
  const today = todayInZone(zone, now);
  const minDate = initialCodeDate({ zone, scheduling, now });
  const skippedDays = Math.round(
    DateTime.fromISO(minDate).diff(DateTime.fromISO(today), "days").days
  );
  return {
    minDate,
    maxDays: Math.max(scheduling.max_days_ahead - skippedDays, 0)
  };
};

export const vendorCodeLabel = (
  lockbox: Pick<Lockbox, "vendor" | "device_type">
) => {
  if (!lockbox.vendor) return "The vendor";
  if (lockbox.vendor === "rently" && lockbox.device_type)
    return `Rently ${DEVICE_TYPE_LABELS[lockbox.device_type]}`;
  return VENDOR_COMPANY_LABELS[lockbox.vendor];
};

const timeRange = (start: DateTime, end: DateTime) =>
  `${start.toFormat(DAY_TIME_FORMAT)} – ${end.toFormat(
    start.hasSame(end, "day") ? TIME_FORMAT : DAY_TIME_FORMAT
  )}`;

export const codeWindowSummary = ({
  lockbox,
  scheduling,
  zone,
  date,
  start,
  durationMinutes
}: {
  lockbox: Pick<Lockbox, "vendor">;
  scheduling: LockboxCodeScheduling;
  zone: string;
  date: string;
  start: DateTime | null;
  durationMinutes: number | null;
}): string | null => {
  if (scheduling.all_day)
    return `Works all day ${formatDay(date, zone)} (property time). Can't be shortened or canceled once created.`;

  if (!start?.isValid) return null;
  const localStart = start.setZone(zone);
  const abbreviation = zoneAbbreviation(localStart, zone);

  if (scheduling.fixed_duration_minutes) {
    const end = localStart.plus({ minutes: scheduling.fixed_duration_minutes });
    return localStart.hasSame(end, "day")
      ? `Works once, ${timeRange(localStart, end)} ${abbreviation}. Can't be canceled.`
      : `Works once, from ${localStart.toFormat(DAY_TIME_FORMAT)} until ${end.toFormat(DAY_TIME_FORMAT)} ${abbreviation}. Can't be canceled.`;
  }

  if (!durationMinutes) return null;
  const end = localStart.plus({ minutes: durationMinutes });
  const cancelHint =
    lockbox.vendor === "sentrilock" && scheduling.sentrilock_cancel_supported
      ? " You can cancel it from the list if plans change."
      : "";
  return `Works ${timeRange(localStart, end)} ${abbreviation}.${cancelHint}`;
};

export const isCodeActive = (
  code: Pick<AccessCode, "valid_from">,
  now: DateTime
) => DateTime.fromISO(code.valid_from).toMillis() <= now.toMillis();

const isCodeExpired = (code: Pick<AccessCode, "valid_until">, now: DateTime) =>
  DateTime.fromISO(code.valid_until).toMillis() <= now.toMillis();

const byWindow = (a: AccessCode, b: AccessCode) =>
  DateTime.fromISO(a.valid_from).toMillis() -
    DateTime.fromISO(b.valid_from).toMillis() ||
  DateTime.fromISO(a.valid_until).toMillis() -
    DateTime.fromISO(b.valid_until).toMillis();

export const visibleCodes = (codes: AccessCode[], now: DateTime) =>
  codes.filter((code) => !isCodeExpired(code, now)).sort(byWindow);

export const codeBoundaries = (codes: AccessCode[]) =>
  codes
    .flatMap((code) => [code.valid_from, code.valid_until])
    .map((iso) => DateTime.fromISO(iso).toMillis())
    .filter((millis) => !Number.isNaN(millis));

export const formatWindow = ({
  validFrom,
  validUntil,
  allDay,
  zone
}: {
  validFrom: string;
  validUntil: string;
  allDay: boolean;
  zone: string;
}) => {
  const start = DateTime.fromISO(validFrom).setZone(zone);
  if (allDay) return `All day ${start.toFormat(DAY_FORMAT)}`;
  const end = DateTime.fromISO(validUntil).setZone(zone);
  return `${timeRange(start, end)} ${zoneAbbreviation(start, zone)}`;
};

// A live code says when it stops working ("Valid until Oct 15, 1:45 PM CDT"); a scheduled one
// spells out its window, as in the design.
const activeCodeCaption = (code: AccessCode, zone: string) => {
  if (code.all_day) return "Valid for the rest of today";
  const until = DateTime.fromISO(code.valid_until).setZone(zone);
  return `Valid until ${until.toFormat(VALID_UNTIL_FORMAT)} ${zoneAbbreviation(until, zone)}`;
};

export const formatCodeWindow = (
  code: AccessCode,
  zone: string,
  now: DateTime
) =>
  isCodeActive(code, now)
    ? activeCodeCaption(code, zone)
    : formatWindow({
        validFrom: code.valid_from,
        validUntil: code.valid_until,
        allDay: code.all_day,
        zone
      });

export const formatShowingWindow = (
  showing: AccessCodeShowing,
  zone: string,
  now: DateTime
) => {
  const start = DateTime.fromISO(showing.start_time).setZone(zone);
  const end = DateTime.fromISO(showing.end_time).setZone(zone);
  const range = `${start.toFormat(TIME_FORMAT)} – ${end.toFormat(TIME_FORMAT)}`;
  return start.hasSame(now.setZone(zone), "day")
    ? range
    : `${start.toFormat(DAY_FORMAT)}, ${range}`;
};

export const buildGeneratePayload = ({
  later,
  scheduling,
  timezone,
  date,
  start,
  durationMinutes,
  recipientName
}: {
  later: boolean;
  scheduling: LockboxCodeScheduling | null;
  timezone: CodeTimezone;
  date: string;
  start: DateTime | null;
  durationMinutes: number | null;
  recipientName: string;
}): GenerateLockboxCodePayload => {
  const recipient = recipientName.trim();
  const base = recipient ? { recipient_name: recipient } : {};
  const durationParam = durationMinutes
    ? { duration_minutes: durationMinutes }
    : {};
  // Sent whenever the property has no time zone, so a Now code is also dated in the user's zone.
  const zoneParam = timezone.isFallback ? { time_zone: timezone.zone } : {};
  if (!later || !scheduling) return { ...base, ...durationParam, ...zoneParam };

  if (scheduling.all_day) return { ...base, ...zoneParam, start_date: date };

  return {
    ...base,
    ...durationParam,
    ...zoneParam,
    starts_at: start?.toUTC().toISO() ?? undefined
  };
};
